/**
 * 編譯期設定。
 *
 * C03 §5 硬性規定：任何 redirect_uri / 分享連結一律用**編譯期注入的 PUBLIC_BASE_URL**
 * 組出來，**絕不**從 `window.location.host` 組。理由：兄弟子網域（rps. / admin.rps. /
 * admin.）若能互相冒用 host 來組 redirect，就等於打通了跨角色的 session 邊界。
 */

export type Locale = 'zh-TW' | 'en';

/**
 * Anonymous identity persistence mode（C03 §5.1）
 *
 * * `cookie` —— 契約 v1.3 的目標狀態。持久身分由**後端發的 httpOnly + Secure +
 *   SameSite=Lax cookie** 提供；前端唯一要做的事是讓「拿 cookie 換 token」那**一個**
 *   請求帶上 cookie（`credentials: 'include'`）。同一個瀏覽器再回來 = 同一個匿名帳號。
 * * `session` —— 落地前的舊行為：身分只活在這個分頁的 `sessionStorage`，
 *   關掉分頁就消失。
 */
export type GuestPersistence = 'cookie' | 'session';

export interface AppConfig {
  oidcIssuer?: string;
  oidcClientId?: string;
  apiBaseUrl: string;
  wsUrl: string;
  publicBaseUrl: string;
  basePath: string;
  gameKey: string;
  useMocks: boolean;
  defaultLocale: Locale;
  buildVersion: string;
  /** sessionStorage key 前綴。C03 §5：每個站點各自獨立的儲存前綴。 */
  storagePrefix: string;
  /**
   * 匿名身分的持久性來源（C03 §5.1）。預設 `cookie` —— 程式碼要表達的是**契約**，
   * 不是過渡狀態。
   *
   * ## ★★ 這個開關存在的唯一理由：它是一個**跨服務的部署順序相依**
   *
   * `cookie` 模式下 `POST /auth/guest` 是**帶憑證的跨來源請求**
   * （`rps.gaming.…` → `api.gaming.…`）。瀏覽器只有在回應同時帶
   * `Access-Control-Allow-Credentials: true` 與**精確 origin** 的
   * `Access-Control-Allow-Origin` 時才會把回應交給 JS。
   *
   * 後端今天是 `allow_credentials=False`（`api/main.py`）⇒
   * **在後端改好之前先部署 `cookie` 模式的前端，換發會 100% 失敗，全站進不去。**
   *
   * 所以：後端先上，前端後上。真的需要讓前端先走一步（或需要把前端單獨回滾）時，
   * 用 `VITE_GUEST_PERSISTENCE=session` build —— 它會同時把行為與**大廳的隱私文案**
   * 一起退回舊的那一組，所以任何一種 build 對玩家講的話都是真的。
   */
  guestPersistence: GuestPersistence;
}

function env(key: string, fallback: string): string {
  const raw = (import.meta.env as Record<string, string | undefined>)[key];
  return raw === undefined || raw === '' ? fallback : raw;
}

export const config: AppConfig = {
  oidcIssuer: env('VITE_OIDC_ISSUER', ''),
  oidcClientId: env('VITE_OIDC_CLIENT_ID', 'gaming-rps-web'),
  apiBaseUrl: env('VITE_API_BASE_URL', 'https://api.gaming.enlightenmentlab.dev/api/v1'),
  wsUrl: env('VITE_WS_URL', 'wss://api.gaming.enlightenmentlab.dev/api/v1/ws'),
  publicBaseUrl: env('VITE_PUBLIC_BASE_URL', 'https://rps.gaming.enlightenmentlab.dev').replace(/\/+$/, ''),
  basePath: env('VITE_PUBLIC_BASE_PATH', '/'),
  gameKey: env('VITE_GAME_KEY', 'gomoku'),
  useMocks: env('VITE_USE_MOCKS', '0') === '1',
  defaultLocale: env('VITE_DEFAULT_LOCALE', 'zh-TW') === 'en' ? 'en' : 'zh-TW',
  buildVersion: env('VITE_BUILD_VERSION', 'dev'),
  storagePrefix: 'elabgaming.gomoku.',
  // 只有明確寫 `session` 才退回舊行為；打錯字（`sesion`、`0`、`off`…）一律當成
  // 契約的目標狀態，不會靜默地把持久身分關掉。
  guestPersistence: env('VITE_GUEST_PERSISTENCE', 'cookie') === 'session' ? 'session' : 'cookie',
};

/**
 * 分享連結一律由編譯期的 publicBaseUrl 組成（C03 §5）。
 * `path` 必須是以單一 `/` 開頭的絕對路徑。
 */
export function absoluteUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const base = config.basePath === '/' ? '' : config.basePath.replace(/\/+$/, '');
  return `${config.publicBaseUrl}${base}${normalized}`;
}
